/**
 * company-theme — entrypoint JS
 *
 * Empaqueta MDB UI Kit Pro + los estilos del tema en un único bundle.
 *
 *   - dist/js/company-theme.min.js      UMD, expone `window.CompanyTheme` (y `window.mdb`)
 *   - dist/js/company-theme.esm.min.js  ESM, para importar componentes sueltos
 *   - dist/css/company-theme.min.css    CSS compilado desde scss/company-theme.scss
 */

import '../scss/company-theme.scss';
import * as mdb from 'mdb-ui-kit-pro/js/mdb.es.min.js';
import { upgradeFloatingLabels, pinNativeDateLabels } from './floating-labels.js';
import { setupPickers } from './pickers.js';
import { setupPickerActions } from './picker-actions.js';
import { setupMonthPickers } from './month-picker.js';
import { setupPhoneInputs } from './phone-input.js';
import { setupCurrencyInputs } from './currency-input.js';
import { setupEmpresaDeLaVentana } from './empresa-de-la-ventana.js';
import { marcarObligatorios } from './obligatorios.js';
import { cuidarEtiquetasLargas } from './etiqueta-larga.js';
import { setupEscaneres } from './escaner-qr.js';
import { montarGraficas, graficaDe } from './grafica.js';
import { montarRedes } from './red.js';
import { cuidarLasCifras } from './cifra-que-cabe.js';
import { mostrarAvisosDelServidor } from './avisos-del-servidor.js';
import { setupCardInputs } from './card-input.js';
import { setupColorInputs } from './color-input.js';
import { setupFileInputs } from './file-input.js';
import { setupAutofill } from './autofill.js';
import { mountNotificationBell, setBellComponents } from './notification-bell.js';
import { notificationList } from './notification-list.js';
import { workspacePicker, setWorkspaceComponents } from './workspace-picker.js';
import { setupDataViews } from './data-view.js';
import { setupFotos } from './fotos.js';
import { form, setFormComponents } from './form.js';
import { setupLayout } from './layout.js';
import { setDialogComponents } from './dialog.js';
// Solo por el efecto: registra en el documento la confirmacion declarativa
// `data-company-confirm`. No hay nada que llamar - ver el fichero -.
import './confirm-action.js';
import { initMdbOn as initMdbOnComponents, refreshMdbOn as refreshMdbOnComponents } from './mdb-init.js';
import { setupRemoteSelects } from './remote-select.js';
import { setupFormGuards } from './form-guard.js';
import { setupNavProgress } from './nav-progress.js';

const { initMDB, ...components } = mdb;

/**
 * Normaliza el markup de formularios (labels flotantes), prepara los selectores
 * de fecha/hora (español + apertura por clic) e inicializa el resto de
 * componentes de MDB sobre los elementos con atributos `data-mdb-*-init`.
 *
 * @param {Object} [extra] Componentes adicionales (plugins Pro, propios) a registrar.
 */
export function autoInit(extra = {}) {
  // Lo primero de todo: con qué empresa trabaja esta ventana. Lo que venga
  // después -las tablas que se refrescan solas, los desplegables remotos- ya
  // sale preguntando por la empresa correcta. Ver `empresa-de-la-ventana.js`.
  setupEmpresaDeLaVentana();

  setDialogComponents(components);
  setFormComponents(components);
  setWorkspaceComponents(components);
  setBellComponents(components);
  upgradeFloatingLabels(document);
  pinNativeDateLabels(document);
  setupPickers(document, components);
  setupPickerActions(components);
  // ANTES de `initMDB`, por dos motivos. Uno: `initMDB` solo escanea el
  // documento la PRIMERA vez que ve cada componente —lleva un registro global
  // `InitializedComponents` y sale antes si ya está—, así que en la segunda
  // llamada no inicializa nada y el markup añadido después se quedaría en
  // crudo. Dos: los desplegables se construyen con las opciones del theme (el
  // buscador encendido), y quien llega primero es quien las pone. Los dos
  // caminos respetan las instancias ya creadas, así que no se pisan.
  initMdbOnComponents(document, components);

  initMDB({ ...components, ...extra });

  // Después de que existan las instancias de Select: se apoya en el desplegable
  // ya pintado.
  setupRemoteSelects(document, components);

  // Después de initMDB: los componentes propios se montan sobre `.form-outline`
  // ya inicializado, para que el label flotante y la muesca del borde existan.
  setupMonthPickers(document);
  setupPhoneInputs(document);
  setupCurrencyInputs(document);
  setupCardInputs(document);
  setupColorInputs(document);
  setupFileInputs(document);

  // Despues de MDB y de los componentes propios: los dos reescriben el DOM de
  // los desplegables, y el rotulo hay que buscarlo en el arbol ya montado.
  //
  // Pone el asterisco en los campos con `required` de las pantallas escritas a
  // mano. Las que arma `form()` ya lo traen; sin esto, el mismo formulario tenia
  // unos campos marcados y otros no segun como se hubiera escrito la pantalla.
  marcarObligatorios(document);

  // Después de MDB, que es quien crea el campo que se vigila. Una ruta larga
  // -«FILETES › SIN ADOBO › FILETE DE 100G › KILOGRAMO»- no cabe en una columna
  // de un tercio, y lo que el navegador esconde es el final: justo la parte que
  // dice cuál de las tres se eligió.
  cuidarEtiquetasLargas(document);

  // Los botones de escanear un QR: con la cámara si es un móvil, y con el
  // relevo al móvil si es un ordenador.
  setupEscaneres(document);

  // Las cifras grandes de los paneles de resumen, encogidas hasta caber. Lo que
  // decide no es el ancho de la pantalla sino cuántos dígitos trae el número.
  cuidarLasCifras(document);

  // Las gráficas. Chart.js no viaja en el paquete: se trae al montar la primera,
  // así que esta llamada no descarga nada en una página sin gráficas.
  montarGraficas(document);

  // Las redes de burbujas. No traen nada de fuera: las posiciones salen de un
  // cálculo corto que se ejecuta una vez y para, así que en una página sin
  // redes esta llamada no hace nada.
  montarRedes(document);

  // Al final: repasa los campos que el navegador rellenó por su cuenta, que es
  // lo que la comprobación única de MDB se pierde según cómo caiga la carrera.
  // Los formularios pintados por el servidor: cancelar pregunta y guardar
  // espera, igual que en los que arma el constructor. Se marcan con
  // `data-company-form-guard`.
  setupFormGuards(document);
  // Delegado en el documento: cubre tambien lo que se pinte despues, asi que
  // no hay que volver a montarlo al reinicializar.
  setupNavProgress(document);

  setupAutofill(document, components);
  setupDataViews(document, components);
  setupFotos(document);
  setupLayout(document, components);

  // Los mensajes que trae el servidor, al final: los avisos se apilan en su
  // esquina y no dependen de nada de arriba, pero salir antes de que la página
  // esté montada los enseñaría sobre una pantalla a medio pintar.
  mostrarAvisosDelServidor(document);
}

/**
 * Reinicializa tras inyectar HTML dinámico (AJAX, Turbo, htmx, Live Components).
 * Es la misma rutina de arranque: los elementos ya montados se saltan y solo se
 * inicializa lo nuevo.
 */
export const reinit = autoInit;

// Arranque automático. Para controlarlo a mano, añadir `data-company-theme-manual`
// al <html> y llamar a CompanyTheme.autoInit() cuando convenga.
if (typeof document !== 'undefined') {
  const manual = document.documentElement.hasAttribute('data-company-theme-manual');

  if (!manual) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => autoInit(), { once: true });
    } else {
      autoInit();
    }
  }

  // Compatibilidad con los snippets de la documentación de MDB y con los
  // plugins Pro (plugins/js/all.min.js), que esperan un global `mdb`.
  if (typeof window !== 'undefined' && !window.mdb) {
    window.mdb = components;
  }
}

// Solo exports con nombre, sin `export default`: así el bundle UMD deja los
// componentes directamente en `window.CompanyTheme.Modal` en vez de obligar a
// escribir `window.CompanyTheme.default.Modal`.
export { upgradeFloatingLabels, pinNativeDateLabels } from './floating-labels.js';
export { setupPickers } from './pickers.js';
export { setupPickerActions } from './picker-actions.js';
export { setupMonthPickers, MonthPicker } from './month-picker.js';
export { setupPhoneInputs, PhoneInput } from './phone-input.js';
export { setupCurrencyInputs, CurrencyInput, setCurrencyDefaults } from './currency-input.js';
export {
  setupEmpresaDeLaVentana,
  empresaDeLaVentana,
  conLaEmpresa,
} from './empresa-de-la-ventana.js';
export { marcarObligatorios } from './obligatorios.js';
export { cuidarEtiquetasLargas } from './etiqueta-larga.js';
export { setupEscaneres, escanearConLaCamara } from './escaner-qr.js';
export { montarGraficas, graficaDe } from './grafica.js';
export { montarRedes, redDe, Red } from './red.js';
export { cuidarLasCifras } from './cifra-que-cabe.js';
export { setupCardInputs, CardInput } from './card-input.js';
export { setupColorInputs, ColorInput, inkOver, normalizeColor } from './color-input.js';
export { setupFileInputs, FileInput } from './file-input.js';
export { setupAutofill } from './autofill.js';
export { workspacePicker, WorkspacePicker } from './workspace-picker.js';
export { mountNotificationBell, NotificationBell, renderNotification } from './notification-bell.js';
export { notificationList, NotificationList } from './notification-list.js';
export { colombia, Colombia } from './colombia.js';
export { updateLive, liveKeys } from './live.js';
export { setupDataViews, DataView } from './data-view.js';
export { form, Form } from './form.js';
export { setupFormGuards } from './form-guard.js';
export { marcarError, limpiarError, limpiarErrores, comprobarNecesarios } from './field-error.js';
export { setupLayout, Layout } from './layout.js';
export { pageNav } from './page-nav.js';
export { setupRemoteSelects, RemoteSelect } from './remote-select.js';

/**
 * Inicializa los componentes de MDB de un trozo de HTML recién añadido. Versión
 * pública de `mdb-init.js`, con los componentes del theme ya puestos.
 *
 * @param {ParentNode} [root=document]
 */
export function initMdbOn(root = document) {
  const resultado = initMdbOnComponents(root, components);

  // Lo que se acaba de añadir tambien lleva campos obligatorios: un renglon
  // nuevo de una factura, una tabla que se repinta. Es idempotente, asi que
  // volver a pasarlo por lo que ya estaba marcado no duplica nada.
  marcarObligatorios(root);
  cuidarEtiquetasLargas(root);
  setupEscaneres(root);
  montarGraficas(root);

  return resultado;
}

/**
 * Vuelve a medir los campos de un trozo que acaba de hacerse visible: una
 * pestaña, el contenido de un modal, un paso de formulario. Hace falta porque
 * MDB mide el label con `clientWidth`, que en un elemento oculto es cero.
 *
 * @param {ParentNode} [root=document]
 */
export function refreshMdbOn(root = document) {
  return refreshMdbOnComponents(root, components);
}
export { createUserMenu, mountUserMenu } from './user-menu.js';
export { setupSidenavSearch, SidenavSearch } from './sidenav-search.js';
export { normalizeText } from './normalize-text.js';
export { notify, Notification } from './notify.js';
export { mostrarAvisosDelServidor, tipoDeAviso } from './avisos-del-servidor.js';
export { setupFotos } from './fotos.js';
export { dialog, confirm } from './dialog.js';
export { CONFIRM_ATTR } from './confirm-action.js';
export { setLocale, getLocale } from './locale.js';
export { COUNTRIES } from './countries.js';
export { placePanel } from './place-panel.js';
export { datepickerES, timepickerES, datetimepickerES, quickActionsES } from './locale-es.js';
export * from 'mdb-ui-kit-pro/js/mdb.es.min.js';
